import {
  DirectOrderTrip,
  DIRECTS_ORDER_FIELDS,
} from "@/stores/direct-order/types";
import { ContactPersonType } from "@/stores/order/types";
import { generateId } from "./utils";
import { ORDER_TYPE } from "@/services/enums/commonEnum";
import { Trip } from "@/stores/trip/types";
import { differenceInDays, differenceInHours, startOfDay } from "date-fns";
import { t } from "@/locales";

/**
 * Creates a new direct order trip with default values
 * @returns {DirectOrderTrip} A new direct order trip object with initialized fields
 */
export const createDirectOrderTrip = (): DirectOrderTrip => ({
  [DIRECTS_ORDER_FIELDS.GEN_TID]: generateId(),
  [DIRECTS_ORDER_FIELDS.ALT_TRIP_NAME]: "",
  [DIRECTS_ORDER_FIELDS.ORDER_TYPE]: ORDER_TYPE.SINGLE,
  [DIRECTS_ORDER_FIELDS.DEPARTURE_DATETIME]: "",
  [DIRECTS_ORDER_FIELDS.TRAVEL_FROM]: "",
  [DIRECTS_ORDER_FIELDS.ROUTE_POINTS]: { oneway: [], return: [] },
  [DIRECTS_ORDER_FIELDS.TRAVEL_TO]: "",
  [DIRECTS_ORDER_FIELDS.ARRIVAL_DATETIME]: "",
  [DIRECTS_ORDER_FIELDS.ARRIVAL_DATETIME_RETURN]: "",
  [DIRECTS_ORDER_FIELDS.BUS_AVAILABILITY]: false,
  [DIRECTS_ORDER_FIELDS.DISTANCE]: { oneway: 0, return: 0 },
  [DIRECTS_ORDER_FIELDS.DURATION]: { oneway: 0, return: 0 },
  [DIRECTS_ORDER_FIELDS.POC]: {} as ContactPersonType,
  [DIRECTS_ORDER_FIELDS.PAX]: "",
  [DIRECTS_ORDER_FIELDS.INVOICE_REF]: "",
  [DIRECTS_ORDER_FIELDS.COMMENT_CUSTOMER]: "",
  [DIRECTS_ORDER_FIELDS.WAITING_TIME]: 0,
  [DIRECTS_ORDER_FIELDS.PRICE_INC_VAT]: 0,
  [DIRECTS_ORDER_FIELDS.PRICE_EX_VAT]: 0,
});

/**
 * Utility function to convert tripsDictionary into "DirectOrderTrip[]"
 * @param tripsDictionary - The order form containing trips as a dictionary
 * @returns {DirectOrderTrip[]} An array of DirectOrderTrip objects
 * This function extracts the trips from the dictionary and returns them as an array.
 */
export const convertOrderFormTypeToTripArray = (
  tripsDictionary: Record<string, DirectOrderTrip>
): DirectOrderTrip[] => {
  return Object.values(tripsDictionary).map((trip) => ({
    ...trip,
  }));
};

/**
 * Checks if a departure time falls within the advance booking warning window.
 * @param {string} startTime - ISO date string of the departure
 * @param {number} warningWindow - The warning window threshold in days
 * @returns {boolean} True if the departure falls within the warning window
 */
export const isTripInBookingWarningWindow = (
  startTime: string | Date,
  warningWindow: number
): boolean => {
  const startDate = new Date(startTime);
  if (Number.isNaN(startDate.getTime())) {
    return false;
  }
  const now = new Date();
  const diffInDays = differenceInDays(startOfDay(startDate), startOfDay(now));
  return diffInDays >= 1 && diffInDays < warningWindow;
};

/**
 * Checks if any trip's departure falls within the advance booking warning window.
 * @param {DirectOrderTrip[]} trips - The list of trips to check
 * @param {number} advanceBookingWarningWindow - The warning window threshold in days
 * @returns {boolean} True if at least one trip falls within the warning window
 */
export const hasBookingNotFinalWarning = (
  trips: DirectOrderTrip[],
  advanceBookingWarningWindow: number
): boolean => {
  return trips.some((trip) =>
    isTripInBookingWarningWindow(
      trip[DIRECTS_ORDER_FIELDS.DEPARTURE_DATETIME],
      advanceBookingWarningWindow
    )
  );
};

/**
 * Returns the appropriate booking-not-final warning message.
 * @param {string} dutyPhone - The duty phone number, if available
 * @returns {string} The warning message
 */
export const getBookingNotFinalMessage = (dutyPhone?: string): string => {
  return dutyPhone
    ? t("order.booking_not_final_warning", { duty_phone: dutyPhone })
    : t("order.booking_not_final_warning_without_phone");
};

export const showCancellationPolicy = (trip: Trip) => {
  if (trip.cancellation_policy) {
    const laterDate = new Date(trip.start_time);
    const earlierDate = new Date();
    const remainingTimeInHours = differenceInHours(laterDate, earlierDate);
    let hours, fee;

    const cancellationPolicyRules = [...trip.cancellation_policy.rules].sort(
      (a, b) => b.hours_before - a.hours_before
    );

    let message = "";

    for (const rule of cancellationPolicyRules) {
      if (remainingTimeInHours <= rule.hours_before && rule.hours_before > 0) {
        hours = rule.hours_before;
        fee = `${rule.fee_value}${
          rule.fee_type === "percentage" ? "%" : " kr"
        }`;
        message = t("trip.cancel_trip_warning", { hours, fee });
      } else {
        break;
      }
    }
    return message;
  }
};
